import { useState } from 'react';
import { Heart, Sparkles, CheckCircle2, XCircle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Profile } from '@/types/profile';
import { useProfileCompatibility } from '@/hooks/useProfileCompatibility';
import { useGems } from '@/hooks/useGems';
import { CalculateCompatibilityDialog } from './CalculateCompatibilityDialog';
import { cn } from '@/lib/utils';

interface ProfileCompatibilityCardProps {
  profile: Profile;
  currentUserProfile?: Profile | null;
}

export function ProfileCompatibilityCard({ 
  profile, 
  currentUserProfile 
}: ProfileCompatibilityCardProps) {
  const { 
    result: compatibilityResult, 
    isLoading, 
    isFetching,
    calculateCompatibility,
    recalculateCompatibility,
    hasExisting 
  } = useProfileCompatibility(profile.id);
  
  const { balance, refreshGems } = useGems();
  const [showDialog, setShowDialog] = useState(false);

  const getCompatibilityColor = (score: number) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-emerald-400';
    if (score >= 40) return 'text-yellow-400';
    return 'text-orange-400';
  };

  const getCompatibilityBgColor = (score: number) => {
    if (score >= 80) return 'from-green-500/20 to-green-500/5';
    if (score >= 60) return 'from-emerald-500/20 to-emerald-500/5';
    if (score >= 40) return 'from-yellow-500/20 to-yellow-500/5';
    return 'from-orange-500/20 to-orange-500/5';
  };

  const canCalculate = currentUserProfile && currentUserProfile.id !== profile.id;

  const handleOpenDialog = () => {
    setShowDialog(true);
  };

  const handleConfirmCalculate = async () => {
    setShowDialog(false);
    if (hasExisting) {
      await recalculateCompatibility();
    } else {
      await calculateCompatibility();
    }
    await refreshGems();
  };

  if (!canCalculate) return null;

  return (
    <>
      <Card className="bg-white/5 border-white/10 p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Heart className="h-4 w-4 text-luxury-pinkBright" />
          <span className="text-sm font-medium text-white">Compatibilidade IA</span>
          <span className="text-xs text-white/40 ml-auto">1 💎</span>
        </div>

        {/* Loading state */}
        {(isFetching || isLoading) && (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin h-5 w-5 border-2 border-luxury-pinkBright border-t-transparent rounded-full" />
            <span className="ml-2 text-white/60 text-sm">
              {isLoading ? 'Analisando perfis...' : 'Carregando...'}
            </span>
          </div>
        )}

        {/* No result yet - show button */}
        {!isFetching && !isLoading && !hasExisting && (
          <Button
            onClick={handleOpenDialog}
            variant="ghost"
            className="w-full bg-gradient-to-r from-[#C4406B] to-[#9F3764] hover:opacity-90 text-white border-0"
          >
            <Sparkles className="h-4 w-4 mr-2 flex-shrink-0" />
            <span className="truncate">Verificar compatibilidade com {profile.username}</span>
          </Button>
        )}

        {/* Show result */}
        {!isFetching && !isLoading && compatibilityResult && (
          <div className="space-y-3">
            {/* Score */}
            <div className={cn(
              "rounded-lg p-4 bg-gradient-to-br",
              getCompatibilityBgColor(compatibilityResult.compatibility_score)
            )}>
              <div className="flex items-center justify-between">
                <span className="text-white/80 text-sm">Score de Compatibilidade</span>
                <span className={cn(
                  "text-3xl font-bold",
                  getCompatibilityColor(compatibilityResult.compatibility_score)
                )}>
                  {compatibilityResult.compatibility_score}%
                </span>
              </div>
              <Progress 
                value={compatibilityResult.compatibility_score} 
                className="h-2 mt-2 bg-white/10"
              />
            </div>

            {/* AI Summary */}
            {compatibilityResult.ai_summary && (
              <p className="text-white/70 text-sm italic">
                "{compatibilityResult.ai_summary}"
              </p>
            )}

            {/* Common Points */}
            {compatibilityResult.common_points && compatibilityResult.common_points.length > 0 && (
              <div className="space-y-1.5">
                <span className="text-xs text-white/50 uppercase tracking-wide">Pontos em comum</span>
                <div className="space-y-1">
                  {compatibilityResult.common_points.map((point, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 text-green-400 mt-0.5 flex-shrink-0" />
                      <span className="text-white/80 text-sm">{point}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Differences */}
            {compatibilityResult.differences && compatibilityResult.differences.length > 0 && (
              <div className="space-y-1.5">
                <span className="text-xs text-white/50 uppercase tracking-wide">Diferenças</span>
                <div className="space-y-1">
                  {compatibilityResult.differences.map((diff, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <XCircle className="h-3.5 w-3.5 text-orange-400 mt-0.5 flex-shrink-0" />
                      <span className="text-white/60 text-sm">{diff}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recalculate button */}
            <Button
              onClick={handleOpenDialog}
              variant="ghost"
              size="sm"
              className="w-full bg-gradient-to-r from-[#C4406B] to-[#9F3764] hover:opacity-90 text-white border-0"
            >
              <Sparkles className="h-3 w-3 mr-1.5" />
              Recalcular (1 💎)
            </Button>
          </div>
        )}
      </Card>

      <CalculateCompatibilityDialog
        open={showDialog}
        onOpenChange={setShowDialog}
        onConfirm={handleConfirmCalculate}
        availableGems={balance}
        targetUsername={profile.username || 'usuário'}
        isLoading={isLoading}
      />
    </>
  );
}
